/*
 * OC Recruitment — Layout Component
 * Design: "The Boardroom" Corporate Modernism — Boutique Retained Search Firm
 * - Midnight Blue (#0F2137) navigation
 * - DM Serif Display headlines / DM Sans body
 * - Contact: chris.kenny@oc-international.com, +44 7366 391 326
 */

import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X, Linkedin, Mail, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/for-companies", label: "For Companies" },
  { href: "/for-candidates", label: "For Candidates" },
  { href: "/process", label: "Our Process" },
  { href: "/case-studies", label: "Case Studies" },
  { href: "/packaging-sectors", label: "Sectors" },
  { href: "/insights", label: "Insights" },
  { href: "/about", label: "About" },
  { href: "/contact", label: "Contact" },
];

const darkHeroPages = [
  "/",
  "/for-companies",
  "/for-candidates",
  "/process",
  "/packaging-sectors",
  "/case-studies",
  "/insights",
  "/about",
  "/contact",
  "/site-map",
];

const OC_LOGO = "/manus-storage/oc-recruitment-logo-optimized_db13998d.webp";

export default function Layout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const hasDarkHero = darkHeroPages.includes(location) || location.startsWith("/insights/") || location.startsWith("/sectors/");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  const navTextBase = scrolled || !hasDarkHero;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-sm"
            : "bg-transparent"
        }`}
      >
        <div className="container">
          <nav className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-4 group shrink-0 mr-5 lg:mr-8">
              <img
                src={OC_LOGO}
                alt="OC Recruitment"
                width={72}
                height={72}
                className="h-14 w-14 object-contain shrink-0 transition-transform duration-300 group-hover:scale-[1.03]"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden xl:flex items-center gap-5 2xl:gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`text-sm font-medium whitespace-nowrap shrink-0 transition-colors duration-200 relative group ${
                    location === link.href
                      ? navTextBase
                        ? "text-midnight"
                        : "text-white"
                      : navTextBase
                      ? "text-slate-brand hover:text-midnight"
                      : "text-white/80 hover:text-white"
                  }`}
                >
                  {link.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-0.5 bg-steel transition-all duration-200 ${
                      location === link.href ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </Link>
              ))}
              <Link
                href="/contact"
                className={`ml-2 px-5 py-2.5 text-sm font-semibold whitespace-nowrap shrink-0 rounded transition-all duration-200 ${
                  navTextBase
                    ? "bg-midnight text-white hover:bg-midnight/90"
                    : "bg-white text-midnight hover:bg-white/90"
                }`}
              >
                Hire Packaging Sales Talent
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`xl:hidden p-2 transition-colors ${
                navTextBase ? "text-midnight" : "text-white"
              }`}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </nav>
        </div>

        {/* Mobile Nav */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="xl:hidden bg-white border-t border-border overflow-hidden"
            >
              <div className="container py-6 flex flex-col gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className={`py-3 px-4 text-sm font-medium rounded transition-colors ${
                      location === link.href
                        ? "text-midnight bg-pale"
                        : "text-slate-brand hover:text-midnight hover:bg-pale/50"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  href="/contact"
                  className="mt-4 px-5 py-3 bg-midnight text-white text-sm font-semibold rounded text-center hover:bg-midnight/90 transition-colors"
                >
                  Hire Packaging Sales Talent
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-midnight text-white">
        <div className="container py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="lg:col-span-1">
              <img
                src={OC_LOGO}
                alt="OC Recruitment"
                width={96}
                height={96}
                className="h-20 w-20 object-contain mb-5"
              />
              <p className="text-white/70 text-sm font-medium mb-4">The Packaging Sales Recruiter.</p>
              <p className="text-white/60 text-xs mb-4">Formerly OC International.</p>
              <p className="text-white/60 text-sm leading-relaxed mb-6">
                UK-based retained search for sales and commercial leadership professionals serving US packaging companies.
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="https://www.linkedin.com/in/packagingsalesrecruiter/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-steel transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin size={20} />
                </a>
                <a
                  href="mailto:chris.kenny@oc-international.com"
                  className="text-white/60 hover:text-steel transition-colors"
                  aria-label="Email"
                >
                  <Mail size={20} />
                </a>
                <a
                  href="tel:+447366391326"
                  className="text-white/60 hover:text-steel transition-colors"
                  aria-label="Phone"
                >
                  <Phone size={20} />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-white/60 mb-6 font-['DM_Sans',sans-serif]">
                Quick Links
              </h2>
              <ul className="space-y-3">
                {navLinks.slice(0, 5).map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="text-sm text-white/60 hover:text-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-white/60 mb-6 font-['DM_Sans',sans-serif]">
                What We Do
              </h2>
              <ul className="space-y-3">
                <li>
                  <Link
                    href="/for-companies"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    Retained Search
                  </Link>
                </li>
                <li>
                  <Link
                    href="/packaging-sectors"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    Packaging Sectors
                  </Link>
                </li>
                <li>
                  <Link
                    href="/case-studies"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    Case Studies
                  </Link>
                </li>
                <li>
                  <Link
                    href="/about"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    About OC Recruitment
                  </Link>
                </li>
                <li>
                  <Link
                    href="/insights"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    Insights & Advice
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-white/60 mb-6 font-['DM_Sans',sans-serif]">
                Get in Touch
              </h2>
              <ul className="space-y-3">
                <li>
                  <a
                    href="mailto:chris.kenny@oc-international.com"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    chris.kenny@oc-international.com
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+13128000868"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    US / Chicago: +1 (312) 800-0868
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+447366391326"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    UK / International: +44 7366 391 326
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.linkedin.com/in/packagingsalesrecruiter/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    LinkedIn Profile
                  </a>
                </li>
              </ul>
              <Link
                href="/contact"
                className="inline-block mt-6 px-5 py-2.5 border border-white/20 text-sm font-medium text-white hover:bg-white/10 rounded transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs text-white/60">
              &copy; 2026 OC Recruitment Ltd. All rights reserved.
            </p>
            <p className="text-xs text-white/60">
              The Packaging Sales Recruiter.
            </p>
            <Link href="/site-map" className="text-xs text-white/60 hover:text-white hover:underline">
              Website Sitemap
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
